import React, { useEffect } from 'react'
import Gap from './Gap'
import { KakaoPlace } from '../api/kakao'
import { setAppStatusBarDark } from '../utils/flutterStatusBar'
import { useSheetDragDismiss } from '../hooks/useSheetDragDismiss'

interface PlacePickBottomSheetProps {
  isOpen: boolean
  candidates: KakaoPlace[]
  onPick: (place: KakaoPlace) => void
  onClose: () => void
}

/**
 * 탭한 자리에 가게가 여러 곳일 때 어디를 고를지 묻는 시트.
 *
 * 왜 필요한가
 * ----------
 * 한 건물에 여러 가게가 있으면 좌표로는 구분할 수 없다. 2층 카페와 3층 식당은
 * 위경도가 완전히 같아서, 가장 가까운 한 곳을 고르는 방식으로는 사실상 아무거나
 * 집게 된다. 좌표가 답을 줄 수 없는 경우이므로 사용자에게 묻는다.
 *
 * 상호만으로는 어느 쪽인지 모를 수 있어 분류(카페·한식 등)를 함께 보여준다.
 * 같은 건물이면 도로명 주소가 똑같이 나오므로 주소는 구분에 도움이 안 된다.
 */
const PlacePickBottomSheet: React.FC<PlacePickBottomSheetProps> = ({
  isOpen,
  candidates,
  onPick,
  onClose,
}) => {
  useEffect(() => {
    setAppStatusBarDark(isOpen)
    return () => setAppStatusBarDark(false)
  }, [isOpen])

  const { handleProps, sheetStyle } = useSheetDragDismiss({
    onClose,
    enabled: isOpen,
  })

  if (!isOpen || candidates.length === 0) {
    return null
  }

  /**
   * '음식점 > 카페 > 커피전문점' 중 사람이 알아보는 마지막 조각만 뽑는다.
   *
   * 다만 프랜차이즈는 마지막 조각이 상호와 똑같이 온다.
   *   메가MGC커피 종로공평점 → '음식점 > 카페 > 커피전문점 > 메가MGC커피'
   * 상호를 한 번 더 보여주는 건 구분에 도움이 안 되므로 한 단계 위를 쓴다.
   */
  const shortCategory = (categoryName: string, placeName: string): string => {
    const parts = categoryName
      .split('>')
      .map((part) => part.trim())
      .filter(Boolean)

    for (let i = parts.length - 1; i >= 0; i--) {
      const part = parts[i]
      if (!placeName.includes(part)) {
        return part
      }
    }

    return ''
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-40 z-[1070]"
        onClick={onClose}
      />

      <div className="fixed inset-0 z-[1070] flex items-end justify-center pointer-events-none">
        <div
          className="app-container bg-ivoryKey rounded-t-30px flex flex-col overflow-hidden pointer-events-auto"
          style={sheetStyle}
          onClick={(e) => e.stopPropagation()}
        >
          {/* 드래그 핸들 (아래로 끌면 닫힘) */}
          <div
            className="w-full flex justify-center pt-16px pb-16px"
            {...handleProps}
          >
            <div className="w-40px h-4px bg-472410-20% rounded-full" />
          </div>

          <p className="px-24px font-pretendard font-bold text-16px leading-24px text-brown-80%-main">
            어느 곳인가요?
          </p>
          <Gap height={4} />
          <p className="px-24px font-pretendard font-regular text-13px leading-140% text-textColorText3">
            이 자리에 {candidates.length}곳이 있어요
          </p>

          <Gap height={12} />

          {/* 후보가 많아도 시트가 화면을 다 덮지 않게 */}
          <div className="w-full max-h-320px overflow-y-auto scrollbar-hide">
            {candidates.map((place) => {
              const category = shortCategory(
                place.category_name || '',
                place.place_name || ''
              )

              return (
                <button
                  key={place.id}
                  type="button"
                  onClick={() => onPick(place)}
                  className="w-full px-24px py-12px bg-transparent border-none cursor-pointer text-left"
                >
                  <p className="font-pretendard font-bold text-16px leading-24px text-brown-80%-main text-ellipsis-1">
                    {place.place_name}
                  </p>
                  {category && (
                    <p className="font-pretendard font-regular text-13px leading-140% text-textColorText3 text-ellipsis-1">
                      {category}
                    </p>
                  )}
                </button>
              )
            })}
          </div>

          <Gap height={33} />
        </div>
      </div>
    </>
  )
}

export default PlacePickBottomSheet
